import React, { useCallback, useMemo } from 'react';

import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

import { AUTHENTICATION_ROUTES } from '@aqx-core/common/constants/router';
import { EDataTestId } from '@aqx-core/common/data-test-id/types';
import { authenticationViewController } from '@aqx-core/common/services/auth/controller';
import { useCompanyProfile } from '@aqx-core/common/services/company';
import { useSignUpDemo } from '@aqx-core/common/services/sign-up-demo';
import { validateCompanyAccess } from '@aqx-core/common/utils/company-access';

import AuthCompletedContent from '../../../shared/completed-content';
import SignUpDemoAccountDetails from './details';

const SignUpDemoCreationCompleted: FCC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const companyProfile = useCompanyProfile();
  const { multiServerEnabled } = companyProfile ?? {};
  const { hasMT4DemoLoginConfig, hasMT5DemoLoginConfig, hasCentroidDemoLoginConfig } = useMemo(() => {
    return validateCompanyAccess(companyProfile);
  }, [companyProfile]);

  const { details } = useSignUpDemo();

  const { login, password } = details || {};

  const onClickLogInButton = useCallback(() => {
    const demoLoginType = authenticationViewController.getDemoLoginTypeFromCompanyAccess({
      multiServerEnabled,
      hasMT4DemoLoginConfig,
      hasMT5DemoLoginConfig,
      hasCentroidDemoLoginConfig
    });

    if (demoLoginType && login && password) {
      authenticationViewController.setPrefilledValues(login, password, demoLoginType);
    }

    navigate(AUTHENTICATION_ROUTES.LOGIN);
  }, [
    hasMT4DemoLoginConfig,
    hasMT5DemoLoginConfig,
    hasCentroidDemoLoginConfig,
    multiServerEnabled,
    login,
    navigate,
    password
  ]);

  return (
    <AuthCompletedContent
      header={t('Demo account is ready')}
      description={t('Your demo account has been opened successfully. We’ve also sent the details to your email.')}
      extraContent={<SignUpDemoAccountDetails />}
      onClickPrimaryButton={onClickLogInButton}
      primaryButtonText={t('Back to Log In')}
      primaryButtonDataTestId={EDataTestId.DEMO_SIGN_UP_BACK_LOG_IN}
    />
  );
};

export default SignUpDemoCreationCompleted;
